import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote, User } from 'lucide-react';

const TestimonialsSection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: 'Baris Taza',
      role: 'Software Engineer, Entrepreneur',
      avatar: 'male',
      text: 'I started working with Ihsan because of persistent low back pain and poor posture that nothing else seemed to fix. From the very first session, he knew exactly which exercises would help, and he tailored the program specifically to my needs. He pushed me just the right amount—not too much to overwhelm me, but enough to keep me progressing every week. Within a short time, my back pain eased and my posture improved dramatically. Friends, family, and coworkers even started commenting on how much taller and more confident I looked. Training with Ihsan has been one of the best investments I\'ve ever made for my health.',
      rating: 5,
      transformation: 'Fixed posture & eliminated back pain'
    },
    {
      name: 'Usame Ceylan',
      role: 'Software Engineer',
      avatar: 'male',
      text: 'Working with my trainer has been one of the best decisions I\'ve ever made for my health and fitness. Not only did I gain noticeable muscle, but my overall body composition completely transformed. I started seeing real changes in my strength, definition, and confidence in the mirror. Every session was structured with the right mix of exercises to challenge me while still keeping me motivated. The results speak for themselves. I feel healthier than ever. I can\'t recommend him enough if you want to see real progress and lasting change.',
      rating: 5,
      transformation: 'Complete body transformation'
    },
    {
      name: 'Fred Hash',
      role: 'Retired VC',
      avatar: 'male',
      text: 'Training with Ihsan for nearly four years transformed my life. Unlike any trainer I\'ve worked with, Ihsan\'s holistic approach went far beyond the gym. His genuine care for my entire lifestyle—meals, sleep quality, and daily well-being—made all the difference. With his infectious passion, he turned fitness into a joyful, sustainable journey, helping me lose nearly 50 pounds, correct years of poor posture, and embrace a healthier mindset that revitalized my life.',
      rating: 5,
      transformation: 'Lost 50 lbs & lifestyle transformation'
    },
    {
      name: 'Paula Bryan',
      role: 'Registered Nurse',
      avatar: 'female',
      text: 'With the help of Ihsan, I found my exercise groove…meaning I have come to love going to the gym to workout, or have found those classes with a community of people I enjoy. It all started with finding someone competent to coach me, to start my exercise journey. I\'m forever grateful for the health gains and benefits I can see.',
      rating: 5,
      transformation: 'Found love for fitness'
    },
    {
      name: 'Alexandra Ksavo',
      role: 'Professional',
      avatar: 'female',
      text: 'Training with Ihsan has been one of the best decisions I\'ve made for my health. From the very first session, he took the time to understand my goals and built a program that was both challenging and effective. He pushes me just the right amount, keeps every workout engaging, and makes sure I\'m moving correctly and safely. The results speak for themselves—I feel stronger, more confident, and more energized in my everyday life. What I appreciate most is his genuine care and accountability. He doesn\'t just train you, he invests in your progress. I can\'t recommend him enough to anyone serious about reaching their fitness goals.',
      rating: 5,
      transformation: 'Stronger & more energized'
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const getAvatar = (avatar: string, name: string) => {
    // Simple avatar with initials
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
    const bgColor = avatar === 'female' ? 'bg-pink-500' : 'bg-blue-500';

    return (
      <div className={`w-24 h-24 rounded-full ${bgColor} flex items-center justify-center border-4 border-primary/50`}>
        <span className="text-white text-2xl font-bold font-montserrat">{initials}</span>
      </div>
    );
  };

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-b from-black to-dark">
      <div className="container mx-auto px-6">
        <h2 className="font-bebas text-5xl md:text-7xl text-center text-white mb-16 tracking-wider">
          CLIENT <span className="text-primary">SUCCESS STORIES</span>
        </h2>

        <div className="max-w-4xl mx-auto">
          {/* Main Testimonial Display */}
          <div className="relative bg-black/50 backdrop-blur-sm p-8 md:p-12 min-h-[500px]">
            <Quote className="absolute top-4 left-4 text-primary/20" size={60} />

            <div className="relative z-10">
              <div className="flex flex-col items-center text-center">
                {/* Profile Avatar */}
                {getAvatar(testimonials[currentIndex].avatar, testimonials[currentIndex].name)}

                {/* Rating Stars */}
                <div className="flex mb-4 mt-6">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <Star key={i} className="text-primary fill-primary" size={20} />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-300 font-montserrat text-base md:text-lg leading-relaxed mb-6 italic max-w-3xl">
                  "{testimonials[currentIndex].text}"
                </p>

                {/* Client Info */}
                <div className="mb-4">
                  <h4 className="text-white font-montserrat font-bold text-xl">
                    {testimonials[currentIndex].name}
                  </h4>
                  <p className="text-gray-400 font-montserrat">
                    {testimonials[currentIndex].role}
                  </p>
                </div>

                {/* Transformation Badge */}
                <div className="bg-primary/20 px-4 py-2 rounded-full">
                  <p className="text-primary font-montserrat text-sm font-semibold">
                    {testimonials[currentIndex].transformation}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-primary transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={32} />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-primary transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight size={32} />
            </button>
          </div>

          {/* Testimonial Indicators */}
          <div className="flex justify-center mt-6 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-primary w-8'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          {/* All Testimonials Grid (Desktop) */}
          <div className="hidden lg:grid grid-cols-5 gap-4 mt-12">
            {testimonials.map((testimonial, index) => {
              const initials = testimonial.name.split(' ').map(n => n[0]).join('').toUpperCase();
              const bgColor = testimonial.avatar === 'female' ? 'bg-pink-500' : 'bg-blue-500';

              return (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`p-4 bg-black/30 backdrop-blur-sm transition-all duration-300 ${
                    index === currentIndex ? 'border-2 border-primary' : 'border-2 border-transparent hover:border-gray-600'
                  }`}
                >
                  <div className={`w-16 h-16 rounded-full ${bgColor} flex items-center justify-center mx-auto mb-2`}>
                    <span className="text-white text-lg font-bold font-montserrat">{initials}</span>
                  </div>
                  <p className="text-white font-montserrat text-sm font-semibold">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-500 font-montserrat text-xs">
                    {testimonial.role}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;